import { FC, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  activeEditor$,
  iconComponentFor$,
  openEditImageDialog$,
  parseImageDimension,
} from '@mdxeditor/editor';
import { useCellValues, usePublisher } from '@mdxeditor/gurx';
import cn from 'clsx';
import { $getNodeByKey } from 'lexical';

import {
  FLOAT_IMAGE_COMMAND,
  getImageFloatFromNode,
  type ImageFloat,
} from './imageFloatPlugin';

import styles from './imageToolbar.module.scss';

type EditImageToolbarProps = {
  nodeKey: string;
  imageSource: string;
  initialImagePath: string | null;
  title: string;
  alt: string;
  width?: number | 'inherit';
  height?: number | 'inherit';
};

const FLOAT_ITEMS: {
  value: ImageFloat;
  title: string;
  icon: 'format_align_left' | 'horizontal_rule' | 'format_align_right';
}[] = [
  { value: 'left', title: 'Float left', icon: 'format_align_left' },
  { value: 'none', title: 'No float', icon: 'horizontal_rule' },
  { value: 'right', title: 'Float right', icon: 'format_align_right' },
];

export const EditImageToolbar: FC<EditImageToolbarProps> = ({
  nodeKey,
  imageSource,
  initialImagePath,
  title,
  alt,
  width,
  height,
}) => {
  const openEditImageDialog = usePublisher(openEditImageDialog$);

  const [activeEditor, iconComponentFor] = useCellValues(
    activeEditor$,
    iconComponentFor$,
  );
  const [editor] = useLexicalComposerContext();
  const [floatValue, setFloatValue] = useState<ImageFloat>('none');

  useEffect(() => {
    editor.getEditorState().read(() => {
      const node = $getNodeByKey(nodeKey);
      if (!node) {
        setFloatValue('none');
        return;
      }
      setFloatValue(getImageFloatFromNode(node));
    });

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const node = $getNodeByKey(nodeKey);
        if (!node) {
          setFloatValue('none');
          return;
        }
        setFloatValue(getImageFloatFromNode(node));
      });
    });
  }, [editor, nodeKey]);

  const handleFloatChange = (value: ImageFloat) => {
    if (!value) {
      return;
    }

    editor.dispatchCommand(FLOAT_IMAGE_COMMAND, {
      nodeKey,
      value,
    });
  };

  return (
    <>
      <div className={styles.actionButtons}>
        <button
          type="button"
          title="Delete image"
          className={styles.iconButton}
          onClick={(e) => {
            e.preventDefault();
            activeEditor?.update(() => {
              $getNodeByKey(nodeKey)?.remove();
            });
          }}
        >
          {iconComponentFor('delete_small')}
        </button>
        <button
          type="button"
          title="Edit image"
          className={styles.iconButton}
          onClick={() => {
            openEditImageDialog({
              nodeKey: nodeKey,
              initialValues: {
                src: initialImagePath ?? imageSource,
                title,
                altText: alt,
                width: parseImageDimension(width),
                height: parseImageDimension(height),
              },
            });
          }}
        >
          {iconComponentFor('settings')}
        </button>
      </div>
      <div className={styles.floatButtons}>
        {FLOAT_ITEMS.map(({ value, title, icon }) => (
          <button
            key={value}
            type="button"
            title={title}
            aria-label={title}
            aria-pressed={floatValue === value}
            onClick={() => handleFloatChange(value)}
            className={cn(styles.iconButton, styles.floatButton, {
              [styles.active]: floatValue === value,
            })}
          >
            {iconComponentFor(icon)}
          </button>
        ))}
      </div>
    </>
  );
};
